import { useCallback, useMemo, useState } from 'react';

import { ENOPCaseStatus, type CaseRotationStatus } from '#entities';
import { useIsModalActive } from '#shared';

import { useSelectIsRotationOn } from '../widgetManager';

import { useHighlightRotation } from './useHighlightRotation';
import { useIsIdle } from './useIsIdle';

export const EXCLUDED_FROM_HIGHLIGHT = new Set<CaseRotationStatus>([
  ENOPCaseStatus.ReturningToBase,
]);

export const useHighlightStatus = () => {
  const [selectedManually, setSelectedManually] =
    useState<CaseRotationStatus | null>(null);
  const isIdle = useIsIdle();
  const isModalActive = useIsModalActive();
  const isRotationOn = useSelectIsRotationOn();

  const shouldClearSelection = isIdle && isRotationOn && !isModalActive;
  const effectiveSelection = shouldClearSelection ? null : selectedManually;

  const { activeRotationStatus } = useHighlightRotation({
    paused: !!effectiveSelection,
  });

  const highlightStatus = useMemo(
    () => effectiveSelection ?? activeRotationStatus,
    [effectiveSelection, activeRotationStatus],
  );

  const toggleStatus = useCallback((s: CaseRotationStatus) => {
    if (EXCLUDED_FROM_HIGHLIGHT.has(s)) {
      return;
    }
    setSelectedManually(prev => (prev === s ? null : s));
  }, []);

  const clearSelection = useCallback(() => setSelectedManually(null), []);
  const onSearchInput = useCallback(() => setSelectedManually(null), []);

  return {
    highlightStatus,
    selectedManually,
    toggleStatus,
    clearSelection,
    onSearchInput,
  };
};
